"use client";

import { cn } from "@shared/lib/cn";
import {
  IconAlertCircleFilled,
  IconCheck,
  IconCopy,
  IconPictureInPictureOff,
  IconPictureInPictureOn,
} from "@tabler/icons-react";
import type { MouseEvent, ReactNode } from "react";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toast as sonnerToast } from "sonner";
import { Button } from "./button";

/** Roughly more than a couple lines in the default toast width. */
const EXPANDABLE_THRESHOLD = 120;

const isExpandableErrorText = (text: string): boolean => {
  const trimmed = text.trim();
  return trimmed.length >= EXPANDABLE_THRESHOLD || trimmed.includes("\n") || /[{}[\]]/.test(trimmed);
};

const copyText = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "fixed";
      textarea.style.left = "-9999px";
      document.body.appendChild(textarea);
      textarea.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(textarea);
      return ok;
    } catch {
      return false;
    }
  }
};

export type ErrorToastAction = {
  label: string;
  onClick: (event: MouseEvent<HTMLButtonElement>) => void;
};

export type ErrorToastCardProps = {
  toastId: string | number;
  title: string;
  description?: string;
  action?: ErrorToastAction;
};

type ErrorToastBodyProps = {
  toastId: string | number;
  title: string;
  description?: string;
  action?: ErrorToastAction;
  canExpand: boolean;
  expanded: boolean;
  onToggleExpand: (event: MouseEvent<HTMLButtonElement>) => void;
  className?: string;
};

const ErrorToastBody = ({
  toastId,
  title,
  description,
  action,
  canExpand,
  expanded,
  onToggleExpand,
  className,
}: ErrorToastBodyProps) => {
  const [copied, setCopied] = useState(false);
  const copyPayload = description ? `${title}\n\n${description}` : title;

  useEffect(() => {
    if (!copied) {
      return;
    }
    const timeout = window.setTimeout(() => setCopied(false), 1500);
    return () => window.clearTimeout(timeout);
  }, [copied]);

  const onCopy = async (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const ok = await copyText(copyPayload);
    if (ok) {
      setCopied(true);
    }
  };

  return (
    <div
      role="alert"
      data-toast-variant="error"
      data-expanded={expanded ? "true" : "false"}
      className={cn(
        "pointer-events-auto flex flex-col overflow-hidden",
        "rounded-(--border-radius,8px) border border-(--normal-border,hsl(0_0%_90.9%)) bg-(--normal-bg,#fff) text-(--normal-text,hsl(0_0%_9%))",
        "text-[13px] shadow-[0_4px_12px_rgba(0,0,0,0.1)]",
        className,
      )}
    >
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pt-4 pb-3">
        <div className="flex items-start gap-3">
          <IconAlertCircleFilled
            className="mt-0.5 size-4 shrink-0 text-(--ds-color-state-error) opacity-80"
            aria-hidden
          />
          <div className="min-w-0 flex-1">
            <p className="wrap-break-word whitespace-pre-wrap font-medium leading-normal">{title}</p>
            {description ? (
              <p className="mt-0.5 wrap-break-word whitespace-pre-wrap font-normal leading-[1.4] text-muted-foreground">
                {description}
              </p>
            ) : null}
          </div>
        </div>
      </div>

      <div className="flex shrink-0 items-center justify-between gap-2 border-t border-(--normal-border,hsl(0_0%_90.9%)) bg-inherit px-4 py-3">
        <div className="flex min-w-0 items-center gap-0.5">
          {canExpand ? (
            <button
              type="button"
              className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground"
              aria-label={expanded ? "Minimize error details" : "Expand error details"}
              onClick={onToggleExpand}
              onPointerDown={(event) => event.stopPropagation()}
            >
              {expanded ? (
                <IconPictureInPictureOn className="size-4" aria-hidden />
              ) : (
                <IconPictureInPictureOff className="size-4" aria-hidden />
              )}
            </button>
          ) : null}
          <button
            type="button"
            className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground"
            aria-label={copied ? "Copied" : "Copy error to clipboard"}
            onClick={onCopy}
            onPointerDown={(event) => event.stopPropagation()}
          >
            {copied ? <IconCheck className="size-4" aria-hidden /> : <IconCopy className="size-4" aria-hidden />}
          </button>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2">
          {action ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={(event) => {
                event.stopPropagation();
                action.onClick(event);
                sonnerToast.dismiss(toastId);
              }}
              onPointerDown={(event) => event.stopPropagation()}
            >
              {action.label}
            </Button>
          ) : null}
          <Button
            type="button"
            size="sm"
            variant="destructive"
            onClick={(event) => {
              event.stopPropagation();
              sonnerToast.dismiss(toastId);
            }}
            onPointerDown={(event) => event.stopPropagation()}
          >
            Dismiss
          </Button>
        </div>
      </div>
    </div>
  );
};

export const ErrorToastCard = ({ toastId, title, description, action }: ErrorToastCardProps) => {
  const combined = description ? `${title}\n${description}` : title;
  const canExpand = isExpandableErrorText(combined);
  const [expanded, setExpanded] = useState(false);
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    if (!expanded) {
      setEntered(false);
      return;
    }

    const frame = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(frame);
  }, [expanded]);

  const onToggleExpand = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setExpanded((value) => !value);
  };

  const compact = (
    <ErrorToastBody
      toastId={toastId}
      title={title}
      description={description}
      action={action}
      canExpand={canExpand}
      expanded={false}
      onToggleExpand={onToggleExpand}
      className="w-(--width,356px) max-w-[calc(100vw-2rem)] max-h-[min(70vh,36rem)]"
    />
  );

  const expandedOverlay =
    expanded && typeof document !== "undefined"
      ? createPortal(
          <div className="pointer-events-none fixed inset-0 z-300 flex items-center justify-center p-[5vh]">
            <div
              className={cn(
                "pointer-events-auto h-full w-full max-h-[90vh] max-w-[90vw] origin-bottom-right transition-[transform,opacity] duration-200 ease-out",
                entered ? "scale-100 opacity-100" : "scale-95 opacity-0",
              )}
              onPointerDown={(event) => event.stopPropagation()}
            >
              <ErrorToastBody
                toastId={toastId}
                title={title}
                description={description}
                action={action}
                canExpand={canExpand}
                expanded
                onToggleExpand={onToggleExpand}
                className="h-full w-full max-h-none max-w-none"
              />
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      {/* Keep the compact toast mounted so Sonner layout stays stable while expanded. */}
      <div className={cn(expanded && "invisible")}>{compact}</div>
      {expandedOverlay}
    </>
  );
};

export const toPlainText = (value: ReactNode): string | null => {
  if (typeof value === "string") {
    return value;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return null;
};
